import React from "react";
import assertNever from "assert-never";
import * as Ons from "react-onsenui";
import { formatPriceUSD } from "../format";
import { Geo, OnboardingVisitId, VisitBroadcastId } from "../messages";
import { STRINGS } from "../strings";
import { Visit } from "../system/AppState/AppVisitsState";
import { globalize, translate } from "../translate";
import {
  getStatusText,
  getStatusType,
  getVisitDuration,
  getVisitStatus,
  getVisitType,
  getVisitTypeText,
  ICON_COLORS_AMOUNT,
} from "./AvailableVisitsAndTasksList";
import { DistanceLabel } from "./DistanceLabel";
import EngagementLabel from "./Generic/EngagementLabel";
import { metersToMilesRounded } from "./VisitListComponents";

interface VisitMarkerPopupProps {
  visit: Visit;
  origin: Geo;
  goToVisit: (visitBroadcastId: VisitBroadcastId) => void;
  goToOnboardingVisit: (onboardingVisitId: OnboardingVisitId) => void;
  goToPendingConfirmationVisit: (
    visitBroadcastId: VisitBroadcastId,
    isVisitConfirmed: boolean
  ) => void;
  visitIconWidth: number;
  visitIconHeight: number;
}

const VisitMarkerPopup = (props: VisitMarkerPopupProps) => {
  const goToVisit = () => {
    switch (props.visit.type) {
      case "Available": {
        const status = getVisitStatus(props.visit);
        if (status === "PENDING_CONFIRMATION") {
          const isVisitConfirmed =
            props.visit.caregiverVisitEngagementDetails.pendingConfirmationConfirmedAt !== null &&
            props.visit.caregiverVisitEngagementDetails.pendingConfirmationConfirmedAt !==
              undefined;
          props.goToPendingConfirmationVisit(props.visit.id, isVisitConfirmed);
        } else {
          props.goToVisit(props.visit.id);
        }
        break;
      }
      case "Onboarding":
        props.goToOnboardingVisit(props.visit.id);
        break;
      default:
        assertNever(props.visit);
    }
  };

  return (
    <div className="AvailableVisitsLiveMap__visitPopover">
      <div className="AvailableVisitsLiveMap__visitPopoverTopPart">
        <div className="AvailableVisitsList__visitMilesLabel" style={{ width: 55 }}>
          <DistanceLabel
            distanceRequest={{
              origin: props.origin,
              destination: props.visit.publicVisitBroadcastDetails.location.location,
            }}
            formatter={metersToMilesRounded}
          />
          <span className="AvailableVisitsList__visitMilesLabelText">
            {translate(STRINGS.MILES_SHORT)}
          </span>
        </div>
        <div
          className={
            "AvailableVisitsList__visitIcon AvailableVisitsLiveMap__smallerPatientInitials AvailableVisitsList__visitIconColor" +
            (props.visit.publicVisitBroadcastDetails.patientInitials.charCodeAt(0) %
              ICON_COLORS_AMOUNT)
          }
        >
          {props.visit.publicVisitBroadcastDetails.patientInitials}
        </div>
        <div className="AvailableVisitsList__visitPrice" style={{ width: 55 }}>
          {props.visit.publicVisitBroadcastDetails.price !== null
            ? formatPriceUSD(props.visit.publicVisitBroadcastDetails.price)
            : ""}
        </div>
      </div>
      <div
        className="AvailableVisitsList__visitDetailsType"
        style={{ marginBottom: 10, textAlign: "center" }}
      >
        <span>{getVisitTypeText(getVisitType(props.visit))}</span>
        {props.visit.publicVisitBroadcastDetails.patientGender !== null ? (
          <span>
            {props.visit.publicVisitBroadcastDetails.patientGender === "M"
              ? translate(STRINGS.MALE)
              : translate(STRINGS.FEMALE)}
          </span>
        ) : (
          ""
        )}
      </div>
      <div className="AvailableVisitsList__visitDetailsDate" style={{ marginBottom: 5 }}>
        <div className="AvailableVisitsList__visitDetailsStartDate">
          <span>
            {globalize().formatLocalDateTime(
              props.visit.publicVisitBroadcastDetails.startTimeLocal,
              { date: "medium" }
            )}
          </span>
          <span>
            {" "}
            {" - " +
              globalize().formatLocalDateTime(
                props.visit.publicVisitBroadcastDetails.endTimeLocal,
                { date: "medium" }
              )}
          </span>
        </div>
      </div>
      <div className="AvailableVisitsList__visitDuration" style={{ marginBottom: 10 }}>
        {getVisitDuration(props.visit)}
      </div>
      <EngagementLabel
        text={getStatusText(getVisitStatus(props.visit))}
        type={getStatusType(getVisitStatus(props.visit))}
        className={
          "AvailableVisitsLiveMap__engagementLabel AvailableVisitsList__visitStatusPill" +
          getVisitStatus(props.visit)
        }
      />
      <Ons.Button
        className={
          "AvailableVisitsLiveMap__goToVisitButton " +
          "AvailableVisitsLiveMap__goToVisitButtonClickable"
        }
        onClick={goToVisit}
      >
        {translate(STRINGS.VISIT_DETAILS)}
      </Ons.Button>
    </div>
  );
};

export default VisitMarkerPopup;
